import { Link } from '@tanstack/react-router';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusDot } from '@/components/ui/status-dot';
import { relTime, truncate, usd } from '@/lib/format';
import { activeDelegations } from '@/lib/org-activity';
import { type ContextKind, summarizeApprovals, summarizeRuns, summarizeTasks } from '@/lib/page-context';
import { useAgents, useApprovals, useRuns, useTasks } from '@/lib/queries';

function Block({ title, link, children }: { title: string; link?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-bg">
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-medium text-fg">{title}</h3>
        {link}
      </header>
      <div className="flex flex-col gap-3 p-3">{children}</div>
    </section>
  );
}

function Tiles({ items }: { items: { label: string; value: ReactNode; warn?: boolean }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((i) => (
        <div key={i.label} className="rounded-lg border border-border px-2.5 py-1.5">
          <div className="text-xs text-muted">{i.label}</div>
          <div
            className={
              i.warn ? 'text-lg font-semibold text-warn tabular-nums' : 'text-lg font-semibold tabular-nums'
            }
          >
            {i.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({ left, right, to }: { left: ReactNode; right?: ReactNode; to?: { runId: string } }) {
  const inner = (
    <>
      <span className="min-w-0 flex-1 truncate text-sm">{left}</span>
      {right ? <span className="shrink-0 text-xs text-muted">{right}</span> : null}
    </>
  );
  return to ? (
    <Link to="/runs/$id" params={{ id: to.runId }} className="flex items-center gap-2 hover:text-accent">
      {inner}
    </Link>
  ) : (
    <div className="flex items-center gap-2">{inner}</div>
  );
}

function RunsContext() {
  const { t } = useTranslation();
  const runs = useRuns();
  const agents = useAgents();
  const s = useMemo(() => summarizeRuns(runs.data ?? []), [runs.data]);
  const name = (id: string) => (agents.data ?? []).find((a) => a.id === id)?.name ?? '—';
  return (
    <Block title={t('inspector.page.runs')}>
      <Tiles
        items={[
          { label: t('inspector.page.active'), value: s.active },
          { label: t('inspector.page.waitingForYou'), value: s.waitingForYou, warn: s.waitingForYou > 0 },
          { label: t('inspector.page.failedToday'), value: s.failedToday, warn: s.failedToday > 0 },
          { label: t('inspector.page.costRunning'), value: usd(s.activeCostUsd, 2) },
        ]}
      />
      <p className="text-xs text-muted">
        {t('inspector.page.runsBreakdown', { running: s.running, queued: s.queued, scheduled: s.scheduled })}
      </p>
      {s.longest.length ? (
        <div className="flex flex-col gap-1.5">
          <div className="text-xs font-medium text-muted">{t('inspector.page.longest')}</div>
          {s.longest.map((r) => (
            <Row
              key={r.id}
              to={{ runId: r.id }}
              left={name(r.agentId)}
              right={relTime(r.startedAt ?? r.createdAt)}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">{t('inspector.page.nothingActive')}</p>
      )}
    </Block>
  );
}

function CompanyContext() {
  const { t } = useTranslation();
  const tasks = useTasks();
  const agents = useAgents();
  const lines = useMemo(() => activeDelegations(tasks.data ?? []), [tasks.data]);
  const name = (id: string) => (agents.data ?? []).find((a) => a.id === id)?.name ?? '—';
  const tone = { running: 'running', approval: 'approval', waiting: 'idle' } as const;
  return (
    <Block title={t('inspector.page.lines')}>
      {lines.length === 0 ? (
        <p className="text-sm text-muted">{t('inspector.page.noLines')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {lines.slice(0, 8).map((l) => {
            const run = (tasks.data ?? []).find((x) => x.id === l.taskId)?.runId;
            return (
              <li key={l.taskId} className="flex flex-col gap-0.5">
                <span className="flex items-center gap-1.5 text-sm">
                  <StatusDot tone={tone[l.state]} label={l.state} />
                  <b className="font-medium">{name(l.from)}</b> → <b className="font-medium">{name(l.to)}</b>
                </span>
                {run ? (
                  <Link
                    to="/runs/$id"
                    params={{ id: run }}
                    className="truncate text-xs text-muted hover:text-accent"
                  >
                    {truncate(l.title, 48)}
                  </Link>
                ) : (
                  <span className="truncate text-xs text-muted">{truncate(l.title, 48)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Block>
  );
}

function ApprovalsContext() {
  const { t } = useTranslation();
  const approvals = useApprovals();
  const s = useMemo(() => summarizeApprovals(approvals.data ?? []), [approvals.data]);
  return (
    <Block title={t('inspector.page.approvals')}>
      {s.pending === 0 ? (
        <p className="text-sm text-muted">{t('inspector.page.nothingWaiting')}</p>
      ) : (
        <>
          <Tiles
            items={[
              { label: t('inspector.page.pending'), value: s.pending, warn: true },
              {
                label: t('inspector.page.soonest'),
                value: s.soonest ? relTime(s.soonest.expiresAt).replace(' ago', '') : '—',
              },
            ]}
          />
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium text-muted">{t('inspector.page.byAgent')}</div>
            {s.byAgent.slice(0, 4).map((a) => (
              <Row key={a.agentId} left={a.name} right={a.count} />
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium text-muted">{t('inspector.page.byTool')}</div>
            {s.byTool.slice(0, 4).map((x) => (
              <Row
                key={x.tool}
                left={<span className="font-mono text-[12.5px]">{x.tool}</span>}
                right={x.count}
              />
            ))}
          </div>
        </>
      )}
    </Block>
  );
}

function TasksContext() {
  const { t } = useTranslation();
  const tasks = useTasks();
  const s = useMemo(() => summarizeTasks(tasks.data ?? []), [tasks.data]);
  return (
    <Block title={t('inspector.page.tasks')}>
      <Tiles
        items={[
          { label: t('inspector.page.open'), value: s.open },
          { label: t('inspector.page.overdue'), value: s.overdue.length, warn: s.overdue.length > 0 },
          { label: t('inspector.page.failedToday'), value: s.failedRecently, warn: s.failedRecently > 0 },
          { label: t('inspector.page.waitingForYou'), value: s.waiting, warn: s.waiting > 0 },
        ]}
      />
      <p className="text-xs text-muted">
        {t('inspector.page.tasksBreakdown', { running: s.running, queued: s.queued })}
      </p>
      {s.overdue.length ? (
        <div className="flex flex-col gap-1.5">
          <div className="text-xs font-medium text-muted">{t('inspector.page.overdue')}</div>
          {s.overdue.slice(0, 3).map((x) => (
            <Row key={x.id} left={x.title} right={x.dueAt ? relTime(x.dueAt) : ''} />
          ))}
        </div>
      ) : null}
    </Block>
  );
}

/** Side-panel block that follows the page: runs, company lines, approvals or tasks. */
export function PageContext({ kind }: { kind: ContextKind }) {
  if (kind === 'runs') return <RunsContext />;
  if (kind === 'company') return <CompanyContext />;
  if (kind === 'approvals') return <ApprovalsContext />;
  if (kind === 'tasks') return <TasksContext />;
  return null;
}
